import { Divider, Image } from 'antd';
import {
  ApplyProduct,
  ApplyProductProps,
} from 'components/Dialogue/components/QAPair/ApplyProduct';
import { formatNumber } from 'utils/numberFormat';
import { useTranslate } from '@tolgee/react';
import { RightOutlined } from '@ant-design/icons';
import { SupplementKnowledgeData } from 'services/api/supplementKnowledge';
import { useIsExpandInCalculation, useUpdateSeekSku } from '../useUpdateSeekSku';
import { ExpandSkuDrawer } from '../SpuMapping/ExpandSkuDrawer';
import useModal from 'hooks/useModal';
import { useEffect } from 'react';

interface ProductInfoProps {
  title: string;
  price: number;
  unit: string;
  imageUrl: string;
  spuId: string;
  skuInfo: ApplyProductProps['skuInfo'];
  skuSelected: ApplyProductProps['skuSelected'];
  onSKUChange: ApplyProductProps['onSKUChange'];
  knowledgeId?: SupplementKnowledgeData['id'];
  expandCount?: SupplementKnowledgeData['expandCount'];
  defaultApplyType?: 'link' | 'sku';
  onDelayRefresh?: () => void;
  onRefresh?: () => void;
}

export const ProductInfo = (props: ProductInfoProps) => {
  const {
    knowledgeId,
    expandCount,
    title,
    price,
    unit,
    imageUrl,
    spuId,
    skuInfo,
    skuSelected,
    onSKUChange,
    onRefresh,
    onDelayRefresh,
    defaultApplyType,
  } = props;
  const { t } = useTranslate();
  const isExpandInCalculation = useIsExpandInCalculation();
  const {
    visible: expandSkuVisible,
    show: showExpandSku,
    hide: hideExpandSku,
  } = useModal<SupplementKnowledgeData['id']>();

  const { updateAsync: updateSeedSkuAsync } = useUpdateSeekSku({
    onRefresh: () => {
      onDelayRefresh?.();
      onRefresh?.();
    },
  });

  useEffect(() => {
    if (!expandSkuVisible) {
      onDelayRefresh?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandSkuVisible]);

  return (
    <div className="bg-[var(--fett-knowledge-product-card-bg)] p-[10px] rounded-sm flex items-center gap-2">
      <Image width={80} height={80} src={imageUrl} alt="product" />
      <div className="flex h-[80px] flex-1 flex-col overflow-hidden justify-between">
        <div className="!my-0 !font-medium overflow-hidden text-ellipsis whitespace-nowrap">
          {title}
          <div className="text-[var(--fett-text-tertiary)] text-[12px]">{`${unit} ${formatNumber(
            price,
            '0,0.00',
          )}`}</div>
        </div>
        <div className="flex items-center justify-end">
          {!!knowledgeId &&
            (isExpandInCalculation(knowledgeId) ? (
              <span className="text-[var(--fett-scenario-accent-text)]">{t('计算中')}</span>
            ) : (
              <span
                className="text-[var(--fett-scenario-accent-text)] cursor-pointer"
                onClick={() => showExpandSku(knowledgeId)}
              >
                {t('扩展同款商品xspuysku', {
                  x: expandCount?.spuCount ?? 0,
                  y: expandCount?.skuCount ?? 0,
                })}
                <RightOutlined />
              </span>
            ))}
          <Divider type="vertical" />
          <ApplyProduct
            v2
            defaultApplyType={defaultApplyType}
            imageUrl={imageUrl}
            skuInfo={skuInfo}
            skuSelected={skuSelected}
            selectable={true}
            onSKUChange={onSKUChange}
            showImage={false}
            onHide={async () => {
              if (knowledgeId) {
                updateSeedSkuAsync({
                  knowledgeId,
                  spuId,
                  skuIds: skuSelected,
                });
              }
            }}
          />
        </div>
      </div>
      {knowledgeId && (
        <ExpandSkuDrawer
          knowledgeId={knowledgeId}
          visible={expandSkuVisible}
          onClose={() => {
            hideExpandSku();
          }}
        />
      )}
    </div>
  );
};
